import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { api, errMsg } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { AuthShell } from "./Login";
import { Loader2, HardHat, Sparkles } from "lucide-react";

// Crew INVITE acceptance page. Admin generates a code from /ops → shares link
// /join?code=CREW-XXXX. This is the ONLY path to a crew account other than
// admin creating one directly.
export default function JoinCrew() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [form, setForm] = useState({ code: params.get("code") || "", name: "", password: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const c = params.get("code");
    if (c && c !== form.code) setForm((f) => ({ ...f, code: c }));
    // eslint-disable-next-line
  }, [params]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/accept-invite", form);
      setUser(data);
      nav("/crew", { replace: true });
    } catch (err) {
      setError(errMsg(err));
    }
    setBusy(false);
  };

  return (
    <AuthShell title="Join the crew" subtitle="Enter your invite code to activate your account.">
      <div className="rounded-xl border border-[var(--cc-accent)]/30 bg-[var(--cc-accent)]/5 p-4 mb-6 flex gap-3">
        <HardHat className="w-5 h-5 text-[var(--cc-accent)] shrink-0 mt-0.5" />
        <p className="text-sm text-muted-foreground leading-relaxed">
          You're joining the crew — you'll see the jobs board, claim missions from
          your tier, and get per-job payouts. Change your password anytime from Settings.
        </p>
      </div>
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="invite-code">Invite code</Label>
          <Input id="invite-code" data-testid="join-code-input" value={form.code}
            onChange={set("code")} placeholder="CREW-XXXXXXXX" className="uppercase font-mono-cc" required />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="join-name">Your name</Label>
          <Input id="join-name" data-testid="join-name-input" value={form.name}
            onChange={set("name")} placeholder="Alex Rivera" required />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="join-phone">Phone (optional)</Label>
          <Input id="join-phone" data-testid="join-phone-input" value={form.phone}
            onChange={set("phone")} placeholder="(619) 555-0100" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="join-password">Create a password</Label>
          <Input id="join-password" data-testid="join-password-input" type="password" value={form.password}
            onChange={set("password")} placeholder="Min 6 characters" required />
        </div>
        {error && <p data-testid="join-error" className="text-sm text-red-500">{error}</p>}
        <Button type="submit" disabled={busy} data-testid="join-submit"
          className="w-full rounded-full h-11 btn-glow bg-[var(--cc-accent)] text-black hover:bg-[var(--cc-accent)]/90 font-semibold">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-2" />Activate Crew Account</>}
        </Button>
      </form>
      <p className="text-sm text-muted-foreground mt-8 text-center">
        No invite? Talk to an admin — public signup is customer-only.<br />
        <Link to="/login" className="text-[var(--cc-accent)] font-semibold">Sign in instead</Link>
      </p>
    </AuthShell>
  );
}
